/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRiskMcpServerCountsRequest,
  GetRiskMcpServerCountsSecurity,
} from "../models/operations/getriskmcpservercounts.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskMcpServerCountsQuery,
  prefetchRiskMcpServerCounts,
  queryKeyRiskMcpServerCounts,
  RiskMcpServerCountsQueryData,
} from "./riskMcpServerCounts.core.js";
export {
  buildRiskMcpServerCountsQuery,
  prefetchRiskMcpServerCounts,
  queryKeyRiskMcpServerCounts,
  type RiskMcpServerCountsQueryData,
};

export type RiskMcpServerCountsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRiskMcpServerCounts risk
 *
 * @remarks
 * Get live finding counts per concrete MCP server over a window, largest first. Powers the MCP server filter pickers on Risk Events and Watchdog. Served from the ClickHouse findings store.
 */
export function useRiskMcpServerCounts(
  request?: GetRiskMcpServerCountsRequest | undefined,
  security?: GetRiskMcpServerCountsSecurity | undefined,
  options?: QueryHookOptions<
    RiskMcpServerCountsQueryData,
    RiskMcpServerCountsQueryError
  >,
): UseQueryResult<RiskMcpServerCountsQueryData, RiskMcpServerCountsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskMcpServerCountsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRiskMcpServerCounts risk
 *
 * @remarks
 * Get live finding counts per concrete MCP server over a window, largest first. Powers the MCP server filter pickers on Risk Events and Watchdog. Served from the ClickHouse findings store.
 */
export function useRiskMcpServerCountsSuspense(
  request?: GetRiskMcpServerCountsRequest | undefined,
  security?: GetRiskMcpServerCountsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskMcpServerCountsQueryData,
    RiskMcpServerCountsQueryError
  >,
): UseSuspenseQueryResult<
  RiskMcpServerCountsQueryData,
  RiskMcpServerCountsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskMcpServerCountsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskMcpServerCountsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskMcpServerCountsQueryData,
): RiskMcpServerCountsQueryData | undefined {
  const key = queryKeyRiskMcpServerCounts(...queryKeyBase);

  return client.setQueryData<RiskMcpServerCountsQueryData>(key, data);
}

export function invalidateRiskMcpServerCounts(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "mcpServerCounts", ...queryKeyBase],
  });
}

export function invalidateAllRiskMcpServerCounts(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "mcpServerCounts"],
  });
}
